/**
 * 세션을 화면에 부르는 이름 — "세션 1", "세션 2".
 *
 * 예전엔 세션 UUID 끝자리("세션 14c6")를 보여 줘 암호처럼 읽혔고, 화면마다 끝 4자리·6자리·전체를 달리 썼다.
 * 세션 id 는 서버가 쓰는 값이지 사람이 부를 이름이 아니다 — 사람에게는 **지금 화면에서 구분만 되면** 된다
 * (사용자 요청 2026-09-13).
 *
 * 규칙
 *  - 번호는 **연결마다** 1 부터. 세션 목록을 받을 때 sessionReducer 가 한 번 매긴다(assignDisplayNumbers).
 *    화면마다 따로 세면 헤더와 탐색기의 번호가 갈린다.
 *  - 한 번 붙은 번호는 목록이 새로 와도 **바뀌지 않는다**. 닫힌 세션의 번호는 가장 작은 빈 번호부터 다시 쓴다
 *    (번호가 계속 커지지 않게).
 *  - 사용자가 붙인 라벨이 있으면 그것을 쓴다.
 *  - SID 는 SID 가 있는 DB(Oracle·Tibero)에서만 보조로 보인다. PostgreSQL·SQLite 에 "sid -" 를 띄우지 않는다.
 */

/** SID(+SERIAL#) 개념이 있는 DB — 세션 식별 보조로 SID 를 보여 줄 수 있다 */
export const SID_DB_TYPES = new Set(['oracle', 'tibero']);

/**
 * 연결마다 가장 작은 빈 번호를 매긴다. 순수 함수 — 이전 번호표와 새 목록을 받아 새 번호표와 번호 붙은 목록을 돌려준다.
 * @param {{ bySession?: Object<string, number> }} numbering 이전 번호표
 * @param {Array} sessions 새 세션 목록 (sessionId, connectionId, createdAt)
 */
export function assignDisplayNumbers(numbering, sessions) {
  const list = Array.isArray(sessions) ? sessions : [];
  const previous = (numbering && numbering.bySession) || {};
  const alive = new Set(list.map((s) => s.sessionId));
  const bySession = {};
  const usedByConn = {};
  const connKey = (s) => s.connectionId || '_';

  // 1) 살아 있는 세션은 이전 번호를 그대로
  for (const s of list) {
    const no = previous[s.sessionId];
    if (no != null && alive.has(s.sessionId)) {
      bySession[s.sessionId] = no;
      (usedByConn[connKey(s)] = usedByConn[connKey(s)] || new Set()).add(no);
    }
  }
  // 2) 새 세션은 만든 순서대로 가장 작은 빈 번호
  const fresh = list
    .filter((s) => bySession[s.sessionId] == null)
    .sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
  for (const s of fresh) {
    const used = (usedByConn[connKey(s)] = usedByConn[connKey(s)] || new Set());
    let no = 1;
    while (used.has(no)) no += 1;
    used.add(no);
    bySession[s.sessionId] = no;
  }
  return {
    numbering: { bySession },
    list: list.map((s) => ({ ...s, displayNo: bySession[s.sessionId] })),
  };
}

/**
 * 독립 연결 창의 전담 세션 — 라벨이 곧 **창 키**다(useOwnConnSession 의 label).
 * 창을 닫아도 세션은 남아 다시 열 때 그대로 쓴다(2026-10-01 결정). 그래서 세션 목록에 오래 보이므로
 * 「session-admin」 같은 키가 아니라 「세션 분석 전용」 으로 부른다.
 */
export const OWN_CONN_WINDOW_KEYS = ['oracle-perf', 'dbms-catalog', 'resource-analysis', 'plsql-editor', 'session-admin', 'lock-wait'];

export function isOwnConnWindowSession(session) {
  return !!session && session.category === 'monitor' && OWN_CONN_WINDOW_KEYS.includes(session.label);
}

/**
 * 이 창이 다시 쓸 전담 세션 — 같은 창 키·같은 연결이고 지금 놀고 있는 것.
 * 실행 중이면 다시 쓰지 않는다(그 세션에 조회를 보내면 409 다). connectionId 를 비우면 연결을 가리지 않는다.
 */
export function findOwnConnSession(list, windowKey, connectionId) {
  return (Array.isArray(list) ? list : []).find(s => (
    s && !s.ephemeral && s.category === 'monitor' && s.label === windowKey
    && (!connectionId || s.connectionId === connectionId)
    && !isSessionBusy(s)
  )) || null;
}

/** 화면에 보일 세션 이름 — 라벨 > "세션 N" */
export function sessionDisplayName(session, t) {
  if (!session) return '';
  if (isOwnConnWindowSession(session)) return t(`common:sp.monitorFor.${session.label}`);
  if (session.label) return session.label;
  return t('common:sp.sessionLabel', { id: session.displayNo != null ? session.displayNo : '?' });
}

/** SID 를 보여 줄 수 있으면 그 값, 아니면 null */
export function sessionSid(session) {
  if (!session || session.dbSid == null) return null;
  return SID_DB_TYPES.has(String(session.dbType || '').toLowerCase()) ? session.dbSid : null;
}

/**
 * 이 세션이 지금 바쁜가 — **판정은 여기 한 곳이다.**
 *
 * 오라클 세션은 한 번에 한 문장만 돈다. 바쁜 세션에 다른 조회를 보내면 서버가 409 로 거절한다
 * (`server.js` 의 `/sessions/:id/execute`). 그래서 화면은 바쁜 세션을 **고를 수 없게** 해야 한다.
 *
 * 예전엔 이 판정이 세 곳(SessionPicker 의 두 자리 · useSessionPolling)에 베껴져 있었다.
 * 갈라지면 어떤 화면은 고르게 하고 어떤 화면은 막아, 고른 쪽만 409 를 받는다.
 *
 * `cancelling` 도 바쁜 것이다 — 취소를 기다리는 동안에도 세션은 비어 있지 않다.
 */
export function isSessionBusy(session) {
  const st = session && session.status;
  return st === 'running' || st === 'cancelling';
}

/**
 * **사람이 열어 둔 세션**인가 — 편집기 탭(`user`)과 독립 연결 창(`monitor` — 서버 값이라 이름이 옛것이다)의 것.
 * 백엔드 `sessionManager.HUMAN_HELD_CATEGORIES` 와 같은 목록이다.
 *
 * 배경 세션(`system-collect` 수집기 · `system-query` 스키마 조회)은 **고를 수 없어야 한다.**
 * 수집기 세션은 timeout 이 10초라, 골라 놓으면 모니터링 조회가 10초에서 끊기는데
 * 화면에는 그냥 실패로만 보인다(실측 2026-09-21: 드롭다운에 collector 세션이 떴다).
 *
 * 카테고리가 없는 옛 세션은 `user` 로 본다(레거시).
 */
export function isHumanSession(session) {
  if (!session) return false;
  const c = session.category;
  return !c || c === 'user' || c === 'monitor';
}
